/**
 * Shared bits of the "Budget, targets & Result definition" tab: labels as LITERAL-key maps (so the
 * locale integrity test sees every key), money and user formatting, month maths.
 *
 * @module components/settings/targets/targetUi
 */
import type { TFunction } from 'i18next';
import { formatCurrency } from '@/config/brand';
import { platformLabel } from '@/utils/platformLabel';
import { TargetWriteError, type TargetErrorCode } from '@/services/targets/targetsService';
import type { ResultMetric } from '@/services/targets/resultMetric';
import type { ProposalStatus, TargetObjective, TargetPlatform } from '@/services/targets/targetRules';

export type TargetRole = 'master' | 'admin' | 'analyst' | 'viewer';

export const STATUS_LABEL: Record<ProposalStatus, (t: TFunction) => string> = {
  proposed: (t) => t('targets.status.proposed'),
  approved: (t) => t('targets.status.approved'),
  rejected: (t) => t('targets.status.rejected'),
  superseded: (t) => t('targets.status.superseded'),
  withdrawn: (t) => t('targets.status.withdrawn'),
};

export const OBJECTIVE_LABEL: Record<TargetObjective, (t: TFunction) => string> = {
  awareness: (t) => t('targets.objective.awareness'),
  consideration: (t) => t('targets.objective.consideration'),
  conversion: (t) => t('targets.objective.conversion'),
};

/** What a Result counts, by metric (DEC3). Literal keys for the locale integrity test. */
export const RESULT_LABEL: Record<ResultMetric, (t: TFunction) => string> = {
  impressions: (t) => t('targets.resultMetric.impressions'),
  reach: (t) => t('targets.resultMetric.reach'),
  clicks: (t) => t('targets.resultMetric.clicks'),
  conversions: (t) => t('targets.resultMetric.conversions'),
};

/** What a Cost per Result is called under that metric: CPM, ต้นทุน / 1,000 reach, CPC, CPA. */
export const COST_LABEL: Record<ResultMetric, (t: TFunction) => string> = {
  impressions: (t) => t('targets.costMetric.impressions'),
  reach: (t) => t('targets.costMetric.reach'),
  clicks: (t) => t('targets.costMetric.clicks'),
  conversions: (t) => t('targets.costMetric.conversions'),
};

const ERROR_LABEL: Record<TargetErrorCode, (t: TFunction) => string> = {
  notAllowed: (t) => t('targets.errors.notAllowed'),
  monthClosed: (t) => t('targets.errors.monthClosed'),
  stale: (t) => t('targets.errors.stale'),
  alreadyDecided: (t) => t('targets.errors.alreadyDecided'),
  effectivePassed: (t) => t('targets.errors.effectivePassed'),
  reasonRequired: (t) => t('targets.errors.reasonRequired'),
  notYours: (t) => t('targets.errors.notYours'),
  monthNotClosed: (t) => t('targets.errors.monthNotClosed'),
  invalidInput: (t) => t('targets.errors.invalidInput'),
  unknown: (t) => t('targets.errors.unknown'),
};

export interface WriteFailure { message: string; offerReload: boolean }

/** A failed write as the sentence to show, and whether only a reload can fix it. */
export const failureOf = (t: TFunction, err: unknown): WriteFailure => {
  const code: TargetErrorCode = err instanceof TargetWriteError ? err.code : 'unknown';
  return { message: ERROR_LABEL[code](t), offerReload: code === 'stale' || code === 'alreadyDecided' };
};

export const reasonRequiredText = (t: TFunction): string => ERROR_LABEL.reasonRequired(t);

export const money = (n: number, currency: string): string => formatCurrency(n, true, currency);

export const keyPlatformLabel = (t: TFunction, platform: TargetPlatform | null): string =>
  platform ? platformLabel(platform) : t('targets.plan.total');

/** A user id as something readable: "you" for the viewer, else a short id (no member can resolve another's name). */
export const whoLabel = (t: TFunction, id: string, me: string | null): string =>
  id === me ? t('targets.history.you') : id.slice(0, 8);

/** The 1st of the month `k` months from the 1st `month` (k may be negative). */
export const shiftMonth = (month: string, k: number): string => {
  const d = new Date(`${month.slice(0, 7)}-01T00:00:00Z`);
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + k, 1)).toISOString().slice(0, 10);
};

/** The most common value, else the fallback. Ties go to the first seen. */
export const mostCommon = (values: readonly string[], fallback: string): string => {
  const counts = values.reduce((m, v) => new Map(m).set(v, (m.get(v) ?? 0) + 1), new Map<string, number>());
  return [...counts.entries()].reduce<[string, number] | null>((best, e) => (best === null || e[1] > best[1] ? e : best), null)?.[0] ?? fallback;
};
